/**
 * Adds a return link to go back to previous page.  This is  based on
 * the query params for `previousPage` and `previousUrl` and will not
 * render if any of these query parameters are missing
 *
 * e.g. /coach?previousPage=Parent&previousUrl=/parent
 */
import {StyleSheet, type CSSProperties} from "aphrodite";
import * as React from "react";

import {View} from "@khanacademy/wonder-blocks-core";
import {PhosphorIcon} from "@khanacademy/wonder-blocks-icon";
import Link from "@khanacademy/wonder-blocks-link";
import {spacing} from "@khanacademy/wonder-blocks-tokens";
import {LabelMedium} from "@khanacademy/wonder-blocks-typography";

import withLocation, {
    type WithLocationProps,
    type WithoutLocation,
} from "#data-access/with-location.tsx";

import caretLeftIcon from "@phosphor-icons/core/bold/caret-left-bold.svg";

type Props = WithLocationProps & {
    style?: CSSProperties;
};

export class MaybeReturnLink extends React.Component<Props> {
    render(): React.ReactNode {
        // NOTE(miguel):
        // queryParams should _always_ be encodeURIComponent.
        const {
            location: {
                queryParams: {previousPage, previousUrl},
            },
            style,
        } = this.props;

        if (!previousPage || !previousUrl) {
            return null;
        }

        return (
            <View style={[styles.container, style]}>
                <Link href={decodeURIComponent(previousUrl)}>
                    <LabelMedium style={styles.text}>
                        <PhosphorIcon
                            icon={caretLeftIcon}
                            style={styles.icon}
                        />
                        {decodeURIComponent(previousPage)}
                    </LabelMedium>
                </Link>
            </View>
        );
    }
}

const styles = StyleSheet.create({
    container: {
        paddingTop: spacing.medium_16,
        paddingBottom: spacing.medium_16,
        maxWidth: 1200, // Matches max width of main navigation header

        // The spacing below is 40 because LP logged-in homepage has max width
        // of 1168 and 24px spacer.  ((1200 - 1168) / 2 ) + 24 = 40
        paddingLeft: 40,
        paddingRight: 40,
        width: "100%",
        margin: "0 auto",
    },
    icon: {
        display: "inline-block",
    },
    text: {
        width: "100%",
        whiteSpace: "nowrap",
        overflow: "hidden",
        textOverflow: "ellipsis",
    },
});

type ExportProps = WithoutLocation<
    JSX.LibraryManagedAttributes<
        typeof MaybeReturnLink,
        React.ComponentProps<typeof MaybeReturnLink>
    >
>;

export default withLocation(
    MaybeReturnLink,
) as React.ComponentType<ExportProps>;
